// src/components/checklists/ChecklistMediaQuestion.js
// chklstweb-3a7f1c58 — photo and signature questions on a job checklist (web).
//
// REUSES the Notes & Media upload path (src/utils/notesMediaUpload.js) unchanged,
// against the INTERNAL note the server makes for this question:
//
//   ensureChecklistMediaNote → cl-{checklistId}-{questionId}     (once per question)
//   validateFile → compressPhoto → createNotesMediaDoc → uploadPhotoToStorage
//
// HOW IT COUNTS. The server counts a photo/signature question as answered when that
// note has at least one notesMedia doc not marked 'failed', and re-counts on every
// notesMedia write (recomputeChecklistOnMediaChange). So the gate needs no answer
// save; the { noteId } answer is saved once only to record where the media lives.
//
// WHAT IT NEVER DOES. It never writes note.media[]: the notes rules let only the
// note's author or a manager update a note, so that write would fail for Office Staff
// and a second technician. Thumbnails come straight from the notesMedia docs instead.
// It never deletes: the rules refuse client deletes of checklist media. A failed or
// cancelled upload is marked uploadStatus 'failed' (the creator may, while it is still
// 'pending') so it stops counting.
//
// REMOVING (chklsthide-4f8b2d61). The × on a saved photo or signature calls hideChecklistMedia:
// the server marks it hidden (who / when), re-counts, and the listener drops it. Nothing is
// deleted. A signature is cleared whole. Office roles see a quiet "N removed — kept on record".
// ATTACHED (chklstmulti-3e9b7c15): the same photo strip on a non-photo question (allowPhotos) — no { noteId } answer is saved and nothing counts.

import React, { useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { Camera, RefreshCw, PenLine, Loader2, X } from 'lucide-react';
import { db, storage } from '../../firebase/config';
import {
    validateFile, compressPhoto, newMediaId, createNotesMediaDoc, uploadPhotoToStorage,
    buildStoragePath, MAX_PHOTOS_PER_NOTE,
} from '../../utils/notesMediaUpload';
import { ensureChecklistMediaNote, mediaNoteId, hideChecklistMedia, isUsableChecklistMedia } from '../../utils/checklistClient';
import SignatureCanvas from './SignatureCanvas';
import ChecklistRemovedMedia from './ChecklistRemovedMedia';

const createdMs = (m) => (m.createdAt?.toMillis ? m.createdAt.toMillis() : Number.MAX_SAFE_INTEGER);
const hiddenMs = (m) => (m.hiddenAt?.toMillis ? m.hiddenAt.toMillis() : 0);

const ChecklistMediaQuestion = ({
    orgId, jobId, checklistId, question, answer, editable, currentUserId, isOffice, canRestore,
    attached, // chklstmulti-3e9b7c15
    onSaveAnswer,      // (questionId, value) — queued through the filler's autosave
    onPresence,        // (questionId, hasMedia) — for the red missing-answer outline
}) => {
    const noteId = mediaNoteId(checklistId, question.id);
    const isSignature = !attached && question.type === 'signature';

    const [mediaDocs, setMediaDocs] = useState([]);   // live notesMedia for this note, not failed
    const [urls, setUrls] = useState({});             // { [mediaId|thumb]: url | 'pending' }
    const [tiles, setTiles] = useState([]);           // this browser's in-flight uploads
    const [signing, setSigning] = useState(false);
    const [signerName, setSignerName] = useState(answer?.signerName || '');
    const [canvasEmpty, setCanvasEmpty] = useState(true);
    const [sigBusy, setSigBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [removingIds, setRemovingIds] = useState(() => new Set()); // media ids showing "Removing…"
    const [removedDocs, setRemovedDocs] = useState([]);              // hidden docs, newest removal first (office list)

    const mountedRef = useRef(true);
    const notePromiseRef = useRef(null);
    const cancelsRef = useRef(new Map());
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        const cancels = cancelsRef.current;
        return () => {
            mountedRef.current = false;
            cancels.forEach((cancel) => cancel());
        };
    }, []);

    // Live media for this question's note.
    useEffect(() => {
        if (!orgId) return undefined;
        const q = query(collection(db, `organizations/${orgId}/notesMedia`), where('noteId', '==', noteId));
        return onSnapshot(q, (snap) => {
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const usable = all.filter(isUsableChecklistMedia).sort((a, b) => createdMs(a) - createdMs(b));
            setMediaDocs(usable);
            setRemovedDocs(all.filter(m => m.hidden === true).sort((a, b) => hiddenMs(b) - hiddenMs(a)));
            // chklsthide-4f8b2d61 — a tile's "Removing…" ends when its hidden doc leaves the list.
            const shownIds = new Set(usable.map(m => m.id));
            setRemovingIds(prev => (prev.size ? new Set([...prev].filter(id => shownIds.has(id))) : prev));
        }, (err) => console.error('ChecklistMediaQuestion: media listener failed', err));
    }, [orgId, noteId]);

    useEffect(() => { onPresence?.(question.id, mediaDocs.length > 0); }, [mediaDocs.length, question.id, onPresence]);

    // Resolve a display URL per media doc: the thumbnail when the server has made one,
    // else the original. A doc whose file has not landed yet (another device still
    // uploading) shows a placeholder and is retried when the doc changes.
    useEffect(() => {
        mediaDocs.forEach((m) => {
            const key = `${m.id}|${m.thumbnailPath || ''}|${m.uploadStatus || ''}`;
            if (urls[key]) return;
            setUrls(prev => ({ ...prev, [key]: 'pending' }));
            getDownloadURL(ref(storage, m.thumbnailPath || buildStoragePath(orgId, noteId, m.id)))
                .then(url => { if (mountedRef.current) setUrls(prev => ({ ...prev, [key]: url })); })
                .catch(() => { if (mountedRef.current) setUrls(prev => ({ ...prev, [key]: 'missing' })); });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaDocs, orgId, noteId]);

    const urlFor = (m) => urls[`${m.id}|${m.thumbnailPath || ''}|${m.uploadStatus || ''}`];

    // The note is made once per question; a retry or a second tab gets the same one.
    const ensureNote = () => {
        if (!notePromiseRef.current) {
            notePromiseRef.current = ensureChecklistMediaNote({ jobId, checklistId, questionId: question.id })
                .then((res) => {
                    const id = res.noteId || noteId;
                    if (!isSignature && !attached && answer?.noteId !== id) onSaveAnswer?.(question.id, { noteId: id });
                    return id;
                })
                .catch((err) => { notePromiseRef.current = null; throw err; });
        }
        return notePromiseRef.current;
    };

    const patchTile = (key, patch) => {
        if (!mountedRef.current) return;
        setTiles(prev => prev.map(t => (t.key === key ? { ...t, ...patch } : t)));
    };
    const dropTile = (key) => {
        if (!mountedRef.current) return;
        setTiles(prev => {
            const t = prev.find(x => x.key === key);
            if (t?.previewUrl) URL.revokeObjectURL(t.previewUrl);
            return prev.filter(x => x.key !== key);
        });
    };

    // One file through the Notes & Media pipeline. Resolves true on success.
    const uploadOne = async (file, key, { compress = true } = {}) => {
        let mediaId = null;
        try {
            validateFile(file);
            patchTile(key, { status: 'compressing', progress: 0, error: '' });
            const ready = compress ? await compressPhoto(file) : file;
            const nid = await ensureNote();
            mediaId = newMediaId();
            await createNotesMediaDoc({ orgId, mediaId, noteId: nid, currentUserId });
            patchTile(key, { status: 'uploading' });
            const { promise, cancel } = uploadPhotoToStorage({
                file: ready, orgId, noteId: nid, mediaId,
                onProgress: (p) => patchTile(key, { progress: p }),
            });
            cancelsRef.current.set(key, cancel);
            await promise;
            cancelsRef.current.delete(key);
            dropTile(key);
            return true;
        } catch (err) {
            cancelsRef.current.delete(key);
            if (mediaId) {
                // Stop a half-made record counting as an answer. Best effort.
                updateDoc(doc(db, `organizations/${orgId}/notesMedia/${mediaId}`), { uploadStatus: 'failed' }).catch(() => {});
            }
            patchTile(key, { status: 'failed', error: err?.message || 'Upload failed.' });
            return false;
        }
    };

    const handleFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setMessage('');
        const room = MAX_PHOTOS_PER_NOTE - mediaDocs.length - tiles.length;
        if (room <= 0) { setMessage(`Up to ${MAX_PHOTOS_PER_NOTE} photos per question.`); return; }
        const take = files.slice(0, room);
        if (files.length > room) setMessage(`Only ${room} more photo${room === 1 ? '' : 's'} can be added here.`);
        const newTiles = take.map((file) => ({
            key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file,
            previewUrl: URL.createObjectURL(file),
            status: 'compressing', progress: 0, error: '',
        }));
        setTiles(prev => [...prev, ...newTiles]);
        newTiles.forEach(t => { uploadOne(t.file, t.key); });
    };

    const retryTile = (t) => uploadOne(t.file, t.key);

    // chklsthide-4f8b2d61 — hide, never delete. "Removing…" shows at once and stays until the
    // listener drops the hidden doc.
    const handleRemove = async (m) => {
        const what = isSignature ? 'signature' : 'photo';
        if (!window.confirm(`Remove this ${what}? It's hidden from the checklist but kept on record.`)) return;
        setMessage('');
        setRemovingIds(prev => new Set(prev).add(m.id));
        try {
            await hideChecklistMedia({ jobId, checklistId, questionId: question.id, mediaId: m.id });
        } catch (err) {
            if (!mountedRef.current) return;
            setRemovingIds(prev => { const next = new Set(prev); next.delete(m.id); return next; });
            setMessage(err?.message || `That ${what} could not be removed. Try again.`);
        }
    };

    const handleSaveSignature = async () => {
        const file = await canvasRef.current?.toJpegFile();
        if (!file) { setMessage('Draw a signature first.'); return; }
        setSigBusy(true);
        setMessage('');
        const key = `sig-${Date.now()}`;
        // 'compressing' renders as "Preparing…" — covers the first-use ensureChecklistMediaNote wait.
        setTiles(prev => [...prev, { key, file, previewUrl: URL.createObjectURL(file), status: 'compressing', progress: 0, error: '' }]);
        const ok = await uploadOne(file, key, { compress: false });
        if (ok) {
            const nid = await ensureNote().catch(() => noteId);
            onSaveAnswer?.(question.id, { noteId: nid, signerName: signerName.trim() || null });
            if (mountedRef.current) { setSigning(false); canvasRef.current?.clear(); }
        }
        if (mountedRef.current) setSigBusy(false);
    };

    // ─── render ─────────────────────────────────────────────────────────────
    const renderTile = (t) => (
        <div key={t.key} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
            <img src={t.previewUrl} alt="" className="w-full h-full object-cover opacity-70" />
            {t.status !== 'failed' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40">
                    <Loader2 size={16} className="animate-spin text-[#5F4EC9]" />
                    <span className="text-[10px] font-semibold text-slate-700 mt-0.5">
                        {t.status === 'compressing' ? 'Preparing…' : 'Uploading…'}
                    </span>
                </div>
            )}
            {t.status === 'uploading' && (
                <div className="absolute bottom-0 left-0 h-1 bg-[#5F4EC9]" style={{ width: `${Math.round((t.progress || 0) * 100)}%` }} />
            )}
            {t.status === 'failed' && (
                <button
                    type="button"
                    onClick={() => retryTile(t)}
                    title={t.error}
                    className="absolute inset-0 flex flex-col items-center justify-center bg-red-50/80 text-red-700"
                >
                    <RefreshCw size={14} />
                    <span className="text-[10px] font-semibold mt-0.5">Try again</span>
                </button>
            )}
        </div>
    );

    const renderSaved = (m) => {
        const url = urlFor(m);
        return (
            <div key={m.id} className={`relative ${isSignature ? 'w-48 h-20' : 'w-20 h-20'} rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center`}>
                {url && url !== 'pending' && url !== 'missing'
                    ? <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                        <img src={url} alt={isSignature ? 'Signature' : 'Checklist photo'} className={`w-full h-full ${isSignature ? 'object-contain bg-white' : 'object-cover'}`} />
                      </a>
                    : <span className="text-[10px] text-slate-400 font-medium">Uploading…</span>}
                {removingIds.has(m.id) ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70">
                        <Loader2 size={16} className="animate-spin text-[#5F4EC9]" />
                        <span className="text-[10px] font-semibold text-slate-700 mt-0.5">Removing…</span>
                    </div>
                ) : editable && (
                    <button
                        type="button"
                        onClick={() => handleRemove(m)}
                        title={isSignature ? 'Remove signature' : 'Remove photo'}
                        aria-label={isSignature ? 'Remove signature' : 'Remove photo'}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-white/90 border border-slate-300 text-slate-600 shadow-sm hover:text-red-600 hover:border-red-300"
                    >
                        <X size={12} />
                    </button>
                )}
            </div>
        );
    };

    // chklsthide-4f8b2d61 — office roles can open what was removed (it's kept on record);
    // Owner / Admin / Manager can restore. Techs and Sales Consultants never see it.
    const removedNote = isOffice ? (
        <ChecklistRemovedMedia
            orgId={orgId} noteId={noteId} jobId={jobId} checklistId={checklistId} questionId={question.id}
            isSignature={isSignature} removed={removedDocs} canRestore={!!canRestore} editable={editable}
        />
    ) : null;

    if (isSignature) {
        const latest = mediaDocs.length ? mediaDocs[mediaDocs.length - 1] : null;
        const pendingTiles = tiles;
        const showPad = editable && (signing || (!latest && pendingTiles.length === 0));
        return (
            <div className="space-y-2">
                {latest && !signing && (
                    <div className="flex items-end gap-3 flex-wrap">
                        {renderSaved(latest)}
                        <div className="text-xs text-slate-500">
                            {answer?.signerName ? <p>Signed by <span className="font-semibold text-slate-700">{answer.signerName}</span></p> : null}
                            {editable && (
                                <button type="button" onClick={() => { setSigning(true); setSignerName(answer?.signerName || ''); }}
                                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#5F4EC9] hover:underline">
                                    <PenLine size={12} /> Sign again
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {pendingTiles.length > 0 && <div className="flex gap-2 flex-wrap">{pendingTiles.map(renderTile)}</div>}
                {!latest && !editable && pendingTiles.length === 0 && (
                    <p className="text-xs text-slate-400 italic">Not signed.</p>
                )}
                {removedNote}
                {showPad && (
                    <div className="space-y-2">
                        <SignatureCanvas ref={canvasRef} disabled={sigBusy} onChange={setCanvasEmpty} />
                        <input
                            type="text"
                            value={signerName}
                            maxLength={120}
                            onChange={e => setSignerName(e.target.value)}
                            placeholder="Signer's name (optional)"
                            className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30"
                        />
                        <div className="flex items-center justify-end gap-2">
                            <button type="button" onClick={() => canvasRef.current?.clear()} disabled={sigBusy || canvasEmpty}
                                className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-40">
                                Clear
                            </button>
                            {latest && (
                                <button type="button" onClick={() => setSigning(false)} disabled={sigBusy}
                                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-40">
                                    Cancel
                                </button>
                            )}
                            <button type="button" onClick={handleSaveSignature} disabled={sigBusy || canvasEmpty}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-[#5F4EC9] hover:bg-[#4f3fb5] rounded-lg disabled:opacity-50">
                                {sigBusy ? 'Saving…' : 'Save signature'}
                            </button>
                        </div>
                    </div>
                )}
                {message && <p className="text-xs text-red-600">{message}</p>}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {(mediaDocs.length > 0 || tiles.length > 0) && (
                <div className="flex gap-2 flex-wrap">
                    {mediaDocs.map(renderSaved)}
                    {tiles.map(renderTile)}
                </div>
            )}
            {mediaDocs.length === 0 && tiles.length === 0 && !editable && !attached && (
                <p className="text-xs text-slate-400 italic">No photos.</p>
            )}
            {removedNote}
            {editable && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={mediaDocs.length + tiles.length >= MAX_PHOTOS_PER_NOTE}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#5F4EC9] border border-[#5F4EC9]/40 rounded-lg hover:bg-[#5F4EC9]/5 disabled:opacity-40"
                    >
                        <Camera size={13} /> Add photos
                    </button>
                </>
            )}
            {message && <p className="text-xs text-red-600">{message}</p>}
        </div>
    );
};

export default ChecklistMediaQuestion;
